<template>
  <v-container fluid class="admin-home-exclusives">
    <div v-if="!isAdmin" class="admin-home-exclusives__blocked">
      <v-icon color="var(--home-accent-strong, #ffcf5a)" size="44">mdi-lock-alert</v-icon>
      <h1>Acesso restrito</h1>
      <p>Esta curadoria esta disponivel apenas para administradores.</p>
      <v-btn class="zinnes-primary-btn" to="/dashboard/painel">Voltar ao painel</v-btn>
    </div>

    <template v-else>
      <AdminExclusiveProjectToolbar
        title="Exclusivos da Home"
        subtitle="Escolha as obras que aparecem na secao Exclusivos e organize a ordem exibida na home."
        :total="exclusiveItems.length"
        :active-count="activeCount"
        :loading="loading"
        :saving="savingOrder"
        :can-save="orderDirty"
        @refresh="loadExclusives"
        @open-search="openSearchModal"
        @save-order="saveOrder"
      />

      <v-alert v-if="feedback.message" :type="feedback.type" dense text class="admin-home-exclusives__feedback">
        {{ feedback.message }}
      </v-alert>

      <v-skeleton-loader
        v-if="loading"
        class="admin-home-exclusives__loading"
        type="article, list-item-avatar-three-line, list-item-avatar-three-line"
      />

      <v-alert v-else-if="loadError" type="error" text class="admin-home-exclusives__feedback">
        Nao foi possivel carregar os exclusivos.
        <v-btn text small color="error" @click="loadExclusives">Tentar novamente</v-btn>
      </v-alert>

      <div v-else-if="!exclusiveItems.length" class="admin-home-exclusives__empty">
        <v-icon size="48" color="var(--home-accent-strong, #ffcf5a)">mdi-star-plus-outline</v-icon>
        <h2>Nenhuma obra exclusiva cadastrada</h2>
        <p>Adicione obras para montar a vitrine exclusiva da home.</p>
        <v-btn class="zinnes-primary-btn" @click="openSearchModal">Adicionar primeira obra</v-btn>
      </div>

      <div v-else class="admin-home-exclusives__list">
        <AdminExclusiveProjectCard
          v-for="(item, index) in exclusiveItems"
          :key="item.id"
          :item="item"
          :index="index"
          :total="exclusiveItems.length"
          :busy="busyItemId === item.id || savingOrder"
          @move-up="moveItem($event, -1)"
          @move-down="moveItem($event, 1)"
          @toggle-active="toggleActive"
          @remove="confirmRemove"
        />
      </div>

      <AdminExclusiveProjectSearchModal
        v-model="searchDialog"
        :query.sync="searchTerm"
        :results="searchResults"
        :selected-project-ids="selectedProjectIds"
        :loading="searching"
        :selecting="Boolean(addingProjectId)"
        :error="searchError"
        :current-page="searchPage"
        :last-page="searchLastPage"
        @search="searchProjects"
        @clear="clearSearch"
        @page-change="searchProjects(searchTerm, $event)"
        @select="addProject"
      />

      <v-dialog v-model="removeDialog" max-width="420px">
        <v-card>
          <v-card-title>Remover exclusivo?</v-card-title>
          <v-card-text>
            A obra sera removida apenas da curadoria da home. O cadastro da obra permanece intacto.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="removeDialog = false">Cancelar</v-btn>
            <v-btn color="error" text :loading="removing" @click="removeSelected">Remover</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </v-container>
</template>

<script>
import "@/styles/home-page/admin.css";

import AdminExclusiveProjectCard from "@/components/home/admin/AdminExclusiveProjectCard.vue";
import AdminExclusiveProjectSearchModal from "@/components/home/admin/AdminExclusiveProjectSearchModal.vue";
import AdminExclusiveProjectToolbar from "@/components/home/admin/AdminExclusiveProjectToolbar.vue";
import AdminHomeExclusivesService from "@/server/AdminHomeExclusivesService";
import { isUserAdmin } from "@/utils/adminMode";
import { normalizeSearchResults, pickArray } from "@/utils/homeProjectNormalizers";

export default {
  name: "AdminHomeExclusivesView",
  components: {
    AdminExclusiveProjectCard,
    AdminExclusiveProjectSearchModal,
    AdminExclusiveProjectToolbar,
  },
  data() {
    return {
      service: new AdminHomeExclusivesService(),
      exclusiveItems: [],
      loading: false,
      loadError: false,
      orderDirty: false,
      savingOrder: false,
      busyItemId: null,
      searchDialog: false,
      searchTerm: "",
      searchResults: [],
      searchPage: 1,
      searchLastPage: 1,
      searching: false,
      searchError: "",
      addingProjectId: null,
      removeDialog: false,
      selectedToRemove: null,
      removing: false,
      feedback: {
        type: "success",
        message: "",
      },
      feedbackTimer: null,
    };
  },
  computed: {
    isAdmin() {
      return isUserAdmin();
    },
    activeCount() {
      return this.exclusiveItems.filter(item => item.active !== false && item.active !== 0).length;
    },
    selectedProjectIds() {
      return this.exclusiveItems
        .map(item => item.project_id || item.project?.id)
        .filter(Boolean)
        .map(id => Number(id));
    },
  },
  mounted() {
    if (this.isAdmin) {
      this.loadExclusives();
    }
  },
  beforeDestroy() {
    window.clearTimeout(this.feedbackTimer);
  },
  methods: {
    async loadExclusives() {
      this.loading = true;
      this.loadError = false;
      try {
        const response = await this.service.list();
        this.exclusiveItems = this.normalizeExclusiveItems(pickArray(response, "exclusive_projects"));
        this.orderDirty = false;
      } catch (error) {
        console.error("Erro ao carregar exclusivos:", error);
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    openSearchModal() {
      this.searchDialog = true;
      if (!this.searchResults.length) {
        this.searchProjects(this.searchTerm, 1);
      }
    },
    async searchProjects(query = this.searchTerm, page = 1) {
      this.searching = true;
      this.searchError = "";
      this.searchTerm = query || "";
      try {
        const response = await this.service.searchProjects({
          name: this.searchTerm || undefined,
          search: this.searchTerm || undefined,
          page,
          per_page: 12,
        });
        const results = pickArray(response, "projects");
        this.searchResults = normalizeSearchResults(results);
        this.searchPage = Number(response?.current_page || page || 1);
        this.searchLastPage = Number(response?.last_page || 1);
      } catch (error) {
        console.error("Erro ao pesquisar obras:", error);
        this.searchError = "Erro ao pesquisar obras.";
      } finally {
        this.searching = false;
      }
    },
    clearSearch() {
      this.searchTerm = "";
      this.searchResults = [];
      this.searchPage = 1;
      this.searchLastPage = 1;
      this.searchError = "";
    },
    async addProject(project) {
      if (this.selectedProjectIds.includes(Number(project.id))) return;
      this.addingProjectId = project.id;
      try {
        await this.service.create(project.id);
        await this.loadExclusives();
        this.showFeedback("success", "Obra adicionada aos Exclusivos.");
      } catch (error) {
        console.error("Erro ao adicionar exclusivo:", error);
        this.showFeedback("error", this.errorMessage(error, "Nao foi possivel adicionar a obra."));
      } finally {
        this.addingProjectId = null;
      }
    },
    moveItem(item, direction) {
      const index = this.exclusiveItems.findIndex(current => current.id === item.id);
      const targetIndex = index + direction;
      if (index < 0 || targetIndex < 0 || targetIndex >= this.exclusiveItems.length) return;

      const nextItems = [...this.exclusiveItems];
      const [movedItem] = nextItems.splice(index, 1);
      nextItems.splice(targetIndex, 0, movedItem);
      this.exclusiveItems = this.normalizeExclusiveItems(nextItems);
      this.orderDirty = true;
    },
    async saveOrder() {
      if (!this.exclusiveItems.length || !this.orderDirty) return;
      this.savingOrder = true;
      try {
        const response = await this.service.reorder(this.orderPayload());
        const updatedItems = pickArray(response, "exclusive_projects");
        if (updatedItems.length) {
          this.exclusiveItems = this.normalizeExclusiveItems(updatedItems);
        }
        this.orderDirty = false;
        this.showFeedback("success", "Ordem salva.");
      } catch (error) {
        console.error("Erro ao salvar ordem:", error);
        this.showFeedback("error", "Nao foi possivel salvar a ordem.");
        await this.loadExclusives();
      } finally {
        this.savingOrder = false;
      }
    },
    async toggleActive(item, active) {
      this.busyItemId = item.id;
      try {
        const response = await this.service.update(item.id, { active: Boolean(active) });
        const updated = response?.exclusive_project || response?.data?.exclusive_project;
        if (updated) {
          this.exclusiveItems = this.normalizeExclusiveItems(
            this.exclusiveItems.map(current => current.id === item.id ? updated : current)
          );
        } else {
          this.exclusiveItems = this.normalizeExclusiveItems(
            this.exclusiveItems.map(current => current.id === item.id ? { ...current, active: Boolean(active) } : current)
          );
        }
        this.showFeedback("success", active ? "Obra ativada na home." : "Obra ocultada da home.");
      } catch (error) {
        console.error("Erro ao atualizar exclusivo:", error);
        this.showFeedback("error", "Nao foi possivel alterar o status.");
        await this.loadExclusives();
      } finally {
        this.busyItemId = null;
      }
    },
    confirmRemove(item) {
      this.selectedToRemove = item;
      this.removeDialog = true;
    },
    async removeSelected() {
      if (!this.selectedToRemove) return;
      this.removing = true;
      this.busyItemId = this.selectedToRemove.id;
      try {
        await this.service.remove(this.selectedToRemove.id);
        this.exclusiveItems = this.normalizeExclusiveItems(
          this.exclusiveItems.filter(item => item.id !== this.selectedToRemove.id)
        );
        this.removeDialog = false;
        this.selectedToRemove = null;
        if (this.exclusiveItems.length) {
          this.orderDirty = true;
          await this.saveOrder();
        }
        this.showFeedback("success", "Obra removida da curadoria.");
      } catch (error) {
        console.error("Erro ao remover exclusivo:", error);
        this.showFeedback("error", "Nao foi possivel remover a obra.");
      } finally {
        this.removing = false;
        this.busyItemId = null;
      }
    },
    normalizeExclusiveItems(items) {
      return items.map((item, index) => ({
        ...item,
        position: index + 1,
      }));
    },
    orderPayload() {
      return this.exclusiveItems.map((item, index) => ({
        id: item.id,
        position: index + 1,
      }));
    },
    showFeedback(type, message) {
      this.feedback = { type, message };
      window.clearTimeout(this.feedbackTimer);
      this.feedbackTimer = window.setTimeout(() => {
        this.feedback.message = "";
      }, 3500);
    },
    errorMessage(error, fallback) {
      return error?.response?.data?.message || error?.message || fallback;
    },
  },
};
</script>
